import { Input } from "./Input"
import type { ColumnDef, Renderable, SortingState } from "@tanstack/react-table"
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table"
import type { ElementType, ReactElement, ReactNode } from "react"
import { Children, cloneElement, Fragment, isValidElement, useMemo, useState } from "react"

import { LINHAS_LISTRADAS, LINHA_SELECIONADA, ROTULO_DO_CABECALHO } from "../lib/classes"
import { cn } from "../lib/cn"
import { useIsMobile } from "../lib/use-is-mobile"
import { Badge } from "./Badge"
import { Button } from "./Button"
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./Dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./DropdownMenu"
import * as Icones from "./Icones"
import { Paginacao } from "./Paginacao"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./Table"

function contarFiltrosAtivos(children: ReactNode): number {
  let total = 0

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return
    }

    const props = child.props as {
      value?: unknown
      defaultValue?: unknown
      selected?: unknown
      children?: ReactNode
    }
    const valorPreenchido = (valor: unknown) =>
      valor !== undefined && valor !== "" && !(Array.isArray(valor) && valor.length === 0)

    if (valorPreenchido(props.value) || valorPreenchido(props.defaultValue) || valorPreenchido(props.selected)) {
      total += 1
    }

    if (props.children) {
      total += contarFiltrosAtivos(props.children)
    }
  })

  return total
}

function classeDaColuna(indiceNaLista: number, temAcoes: boolean): string | undefined {
  if (temAcoes && indiceNaLista === 0) {
    return "w-px whitespace-nowrap"
  }

  const indiceDeDado = indiceNaLista - (temAcoes ? 1 : 0)

  return indiceDeDado > 0 ? "hidden md:table-cell" : undefined
}

function ariaSort(
  ordenavel: boolean,
  direcao: false | "asc" | "desc",
): "none" | "ascending" | "descending" | undefined {
  if (!ordenavel) {
    return undefined
  }

  if (direcao === "asc") {
    return "ascending"
  }

  return direcao === "desc" ? "descending" : "none"
}

function resolverPorLinha<TData>(
  valor: boolean | ((row: TData) => boolean) | undefined,
  row: TData,
  padrao: boolean,
): boolean {
  if (typeof valor === "function") {
    return valor(row)
  }

  return valor ?? padrao
}

interface ColumnProps<TData> {
  name: string
  label?: string
  render?: (value: unknown, row: TData) => ReactNode
  enableSorting?: boolean
  classe?: string
}

function Column<TData>(_props: ColumnProps<TData>) {
  return null
}
Column.displayName = "Column"

interface AcaoProps<TData> {
  label: string
  icon?: ElementType
  onClick?: (row: TData) => void
  disabled?: boolean | ((row: TData) => boolean)
  visivel?: boolean | ((row: TData) => boolean)
  motivo?: string | ((row: TData) => string | undefined)
  inline?: boolean
}

function Acao<TData>(_props: AcaoProps<TData>) {
  return null
}
Acao.displayName = "Acao"

interface FiltroGlobalProps {
  globalFilter: string
  setGlobalFilter: (value: string) => void
  placeholder?: string
}

function TablePrincipalFiltroGlobal({ globalFilter, setGlobalFilter, placeholder = "Pesquisar..." }: FiltroGlobalProps) {
  const [valorLocal, setValorLocal] = useState(globalFilter ?? "")
  const [filtroAnterior, setFiltroAnterior] = useState(globalFilter)

  if (globalFilter !== filtroAnterior) {
    setFiltroAnterior(globalFilter)
    setValorLocal(globalFilter ?? "")
  }

  return (
    <div className="relative flex min-w-64 flex-1 items-center">
      <span className="pointer-events-none absolute left-3 flex text-muted-foreground">
        <Icones.Buscar tamanho={16} />
      </span>
      <Input
        value={valorLocal}
        onChange={(e) => {
          setValorLocal(e.target.value)
          setGlobalFilter(e.target.value)
        }}
        placeholder={placeholder}
        aria-label="Pesquisar na tabela"
        className="h-10 w-full rounded-md border border-input bg-background py-2 pr-3 pl-9 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:border-ring md:max-w-[364px]"
      />
    </div>
  )
}
TablePrincipalFiltroGlobal.displayName = "TablePrincipalFiltroGlobal"

function TableFiltrosAvancados({ children }: { children: ReactNode }) {
  return <>{children}</>
}
TableFiltrosAvancados.displayName = "TableFiltrosAvancados"

type TablePrincipal2Props<TData> = {
  data: TData[]
  children: ReactNode
  paginacao?: boolean
  pageSize?: number
  pageCount: number
  currentPage: number
  totalRegistros?: number
  onPageChange: (page: number) => void
  onSortingChange?: (sorting: SortingState) => void
  onGlobalFilterChange?: (filter: string) => void
  globalFilterValue?: string
  onPageSizeChange?: (size: number) => void
  onApplyAdvancedFilters?: () => void
  onClearFilters?: () => void
  temAbas?: ReactNode
  acaoNoTopo?: ReactNode
  filtrosNaBarra?: ReactNode
  abasNaBarra?: ReactNode
  mensagemVazia?: ReactNode
  listrada?: boolean
  aoClicarNaLinha?: (linha: TData) => void
  linhaClicavel?: (linha: TData) => boolean
  detalheDaLinha?: (linha: TData) => ReactNode
  linhaAberta?: (linha: TData) => boolean
  linhaSelecionada?: (linha: TData) => boolean
  ordenacaoNoServidor?: boolean
  ordenacao?: SortingState
}

function desenharCelula<TProps extends object>(conteudo: Renderable<TProps>, contexto: TProps): ReactNode {
  return typeof conteudo === "function" ? (conteudo as (props: TProps) => ReactNode)(contexto) : flexRender(conteudo, contexto)
}

function TablePrincipal2<TData>({
  data,
  children,
  paginacao = true,
  pageCount,
  currentPage,
  pageSize = 15,
  totalRegistros = 0,
  globalFilterValue,
  onPageChange,
  onPageSizeChange,
  onSortingChange,
  onGlobalFilterChange,
  onApplyAdvancedFilters,
  onClearFilters,
  temAbas,
  acaoNoTopo,
  filtrosNaBarra,
  abasNaBarra,
  mensagemVazia = "Nenhum dado encontrado.",
  aoClicarNaLinha,
  linhaClicavel,
  detalheDaLinha,
  linhaAberta,
  linhaSelecionada,
  ordenacaoNoServidor = false,
  ordenacao,
  listrada = false,
}: TablePrincipal2Props<TData>) {
  const [ordenacaoInterna, setSorting] = useState<SortingState>([])
  const sorting = ordenacao ?? ordenacaoInterna
  const [globalFilter, setGlobalFilter] = useState("")

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [expandidas, setExpandidas] = useState<Record<string, boolean>>({})
  const [paginaAnterior, setPaginaAnterior] = useState(currentPage)
  const isMobile = useIsMobile()
  const aberturaPelaTela = detalheDaLinha !== undefined && linhaAberta !== undefined

  if (currentPage !== paginaAnterior) {
    setPaginaAnterior(currentPage)
    setExpandidas({})
  }

  const clicavel = (linha: TData) => aoClicarNaLinha !== undefined && (linhaClicavel?.(linha) ?? true)
  const abertaPelaTela = (linha: TData) => aberturaPelaTela && (aoClicarNaLinha === undefined || clicavel(linha))
  const precisaDaSeta = (linha: TData) => isMobile && !abertaPelaTela(linha)
  const botaoDeExpandir = isMobile && data.some(precisaDaSeta)

  const alternarLinha = (rowId: string) => setExpandidas((atual) => ({ ...atual, [rowId]: !atual[rowId] }))

  const globalFilterSlot = Children.toArray(children).find(
    (child) =>
      isValidElement(child) &&
      (child.type as { displayName?: string }).displayName === "TablePrincipalFiltroGlobal",
  )

  const advancedFiltersSlot = Children.toArray(children).find(
    (child) =>
      isValidElement(child) && (child.type as { displayName?: string }).displayName === "TableFiltrosAvancados",
  ) as ReactElement<{ children: ReactNode }> | undefined

  const temAcoes = Children.toArray(children).some(
    (child) => isValidElement(child) && (child.type as { displayName?: string }).displayName === "Acao",
  )
  const filtroGlobalAtual = globalFilterValue ?? globalFilter
  const filtrosAtivos = contarFiltrosAtivos(advancedFiltersSlot?.props.children)
  const botaoDeFiltros = advancedFiltersSlot && (
    <Button variant="outline" onClick={() => setIsFilterModalOpen(true)}>
      <Icones.Filtrar tamanho={16} />
      Filtros
      {filtrosAtivos > 0 && <Badge>{filtrosAtivos}</Badge>}
    </Button>
  )

  const colunasDeDados = useMemo(
    () =>
      Children.toArray(children)
        .filter(
          (child) => isValidElement(child) && (child.type as { displayName?: string }).displayName === "Column",
        )
        .map((child) => (child as ReactElement<ColumnProps<TData>>).props),
    [children],
  )

  const classeDaColunaPelaTela = useMemo(
    () => new Map(colunasDeDados.map((coluna) => [coluna.name, coluna.classe])),
    [colunasDeDados],
  )

  const columnDefs = useMemo(() => {
    const cols: ColumnDef<TData>[] = []
    const actions: AcaoProps<TData>[] = []

    Children.forEach(children, (child) => {
      if (!isValidElement(child)) {
        return
      }

      const displayName = (child.type as { displayName?: string }).displayName

      if (displayName === "Column") {
        const props = child.props as ColumnProps<TData>
        cols.push({
          accessorKey: props.name,
          enableSorting: props.enableSorting !== false,
          header: ({ column }) => {
            const rotulo = (
              <span className={ROTULO_DO_CABECALHO}>
                {props.label ?? props.name}
              </span>
            )

            if (!column.getCanSort()) {
              return rotulo
            }

            const direcao = column.getIsSorted()
            const IconeOrdem =
              direcao === "asc" ? Icones.Diminuir : direcao === "desc" ? Icones.Aumentar : Icones.OrdenarColunas

            return (
              <button
                type="button"
                className="group flex cursor-pointer items-center gap-1 select-none focus-visible:outline-2 focus-visible:outline-ring"
                onClick={column.getToggleSortingHandler()}
              >
                {rotulo}
                <span
                  className={cn(
                    "flex text-muted-foreground transition-opacity motion-reduce:transition-none",
                    !direcao && "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
                  )}
                >
                  <IconeOrdem tamanho={12} />
                </span>
              </button>
            )
          },
          cell: ({ row, getValue }) => (
            <div className="text-sm text-foreground">
              {props.render ? props.render(getValue(), row.original) : (getValue() as ReactNode)}
            </div>
          ),
        })
      } else if (displayName === "Acao") {
        actions.push(child.props as AcaoProps<TData>)
      }
    })

    if (actions.length > 0) {
      cols.unshift({
        id: "actions",
        enableSorting: false,
        header: () => (
          <span className={cn("block text-center", ROTULO_DO_CABECALHO)}>Ações</span>
        ),
        cell: ({ row }) => {
          const acoesVisiveis = actions.filter((acao) => resolverPorLinha(acao.visivel, row.original, true))

          if (acoesVisiveis.length === 0) {
            return (
              <div className="flex items-center justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled
                  aria-label="Nenhuma ação disponível nesta linha"
                  title="Nenhuma ação disponível nesta linha"
                  className="disabled:bg-transparent"
                >
                  <Icones.Configuracao tamanho={16} />
                </Button>
              </div>
            )
          }

          const foraDoMenu = acoesVisiveis.filter((acao) => acao.inline)
          const noMenu = acoesVisiveis.filter((acao) => !acao.inline)

          return (
            <div className="flex items-center justify-center gap-1">
              {foraDoMenu.map((acao) => {
                const desabilitada = resolverPorLinha(acao.disabled, row.original, false)
                const motivo = desabilitada ? (typeof acao.motivo === "function" ? acao.motivo(row.original) : acao.motivo) : undefined
                const Icon = acao.icon

                return (
                  <Button
                    key={acao.label}
                    variant="ghost"
                    size={Icon ? "icon-sm" : "sm"}
                    aria-label={acao.label}
                    title={motivo ?? acao.label}
                    disabled={desabilitada}
                    onClick={() => acao.onClick?.(row.original)}
                  >
                    {Icon ? <Icon className="size-4" /> : <span>{acao.label}</span>}
                  </Button>
                )
              })}
              {noMenu.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Abrir menu de ações"
                      title="Abre as ações desta linha"
                      className="text-foreground hover:bg-azul-200 hover:text-(--sobre-azul-100) data-[state=open]:bg-(--azul-100) data-[state=open]:text-(--sobre-azul-100)"
                    >
                      <Icones.Configuracao tamanho={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {noMenu.map((acao) => {
                      const isDisabled = resolverPorLinha(acao.disabled, row.original, false)
                      const motivo = isDisabled
                        ? typeof acao.motivo === "function"
                          ? acao.motivo(row.original)
                          : acao.motivo
                        : undefined
                      const Icon = acao.icon

                      return (
                        <DropdownMenuItem
                          key={acao.label}
                          disabled={isDisabled}
                          onClick={() => {
                            if (!isDisabled) {
                              acao.onClick?.(row.original)
                            }
                          }}
                        >
                          {Icon && <Icon />}
                          {motivo === undefined ? (
                            <span>{acao.label}</span>
                          ) : (
                            <span className="flex flex-col">
                              <span>{acao.label}</span>
                              <span className="max-w-64 text-xs text-muted-foreground">{motivo}</span>
                            </span>
                          )}
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )
        },
      })
    }

    return cols
  }, [children])

  const table = useReactTable({
    data,
    columns: columnDefs,
    pageCount,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: ordenacaoNoServidor,
    state: {
      pagination: { pageIndex: currentPage - 1, pageSize },
      globalFilter: filtroGlobalAtual,
      sorting,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater
      setSorting(next)
      onSortingChange?.(next)
    },
    onGlobalFilterChange: (value) => {
      setGlobalFilter(value)
      onGlobalFilterChange?.(value)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="w-full">
      <Dialog open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Filtros avançados</DialogTitle>
          </DialogHeader>
          <DialogBody className="grid gap-4">{advancedFiltersSlot?.props.children}</DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              className="text-destructive hover:bg-muted hover:text-destructive"
              onClick={() => {
                onClearFilters?.()
                setIsFilterModalOpen(false)
              }}
            >
              Limpar filtros
            </Button>
            <Button
              onClick={() => {
                onApplyAdvancedFilters?.()
                setIsFilterModalOpen(false)
              }}
            >
              Aplicar filtros
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(isValidElement(globalFilterSlot) || acaoNoTopo || botaoDeFiltros || filtrosNaBarra || abasNaBarra) && (
        <div className="flex flex-wrap items-center gap-2 pb-2">
          {abasNaBarra && (
            <div data-slot="abas-na-barra" className="flex max-w-full min-w-0 items-center">
              {abasNaBarra}
            </div>
          )}
          {isValidElement(globalFilterSlot) &&
            cloneElement(globalFilterSlot as ReactElement<FiltroGlobalProps>, {
              globalFilter: filtroGlobalAtual,
              setGlobalFilter: (value: string) => {
                setGlobalFilter(value)
                onGlobalFilterChange?.(value)
              },
            })}
          {filtrosNaBarra && (
            <div data-slot="filtros-na-barra" className="flex flex-wrap items-center gap-2">
              {filtrosNaBarra}
            </div>
          )}
          {botaoDeFiltros}
          {acaoNoTopo && <div className="ml-auto flex flex-wrap items-center justify-end gap-2 max-md:w-full">{acaoNoTopo}</div>}
        </div>
      )}

      {temAbas ? <div className="pb-2">{temAbas}</div> : null}

      <div data-slot="tabela-principal" className="w-full max-w-full overflow-x-auto rounded-sm border">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {botaoDeExpandir && (
                  <TableHead className="w-10">
                    <span className="sr-only">Expandir</span>
                  </TableHead>
                )}
                {headerGroup.headers.map((header, indice) => (
                  <TableHead
                    key={header.id}
                    aria-sort={ariaSort(header.column.getCanSort(), header.column.getIsSorted())}
                    className={cn(classeDaColuna(indice, temAcoes), classeDaColunaPelaTela.get(header.column.id))}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className={listrada ? LINHAS_LISTRADAS : undefined}>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    data-clicavel={clicavel(row.original) ? "true" : undefined}
                    data-state={linhaSelecionada?.(row.original) === true ? "selected" : undefined}
                    data-listra={linhaSelecionada?.(row.original) === true ? "nao" : undefined}
                    aria-selected={linhaSelecionada === undefined ? undefined : linhaSelecionada(row.original)}
                    className={cn(
                      clicavel(row.original) && "cursor-pointer",
                      linhaSelecionada?.(row.original) === true && LINHA_SELECIONADA,
                    )}
                    onClick={
                      aoClicarNaLinha === undefined || !clicavel(row.original)
                        ? undefined
                        : (evento) => {
                            const alvo = evento.target as HTMLElement
                            if (!evento.currentTarget.contains(alvo)) return
                            if (alvo.closest("button, a, input, select, textarea, [role=menuitem]")) return
                            if ((window.getSelection()?.toString() ?? "") !== "") return
                            aoClicarNaLinha(row.original)
                          }
                    }
                  >
                    {botaoDeExpandir && !precisaDaSeta(row.original) && <TableCell className="w-10" />}
                    {botaoDeExpandir && precisaDaSeta(row.original) && (
                      <TableCell className="w-10">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="size-9"
                          aria-label={expandidas[row.id] ? "Recolher linha" : "Expandir linha"}
                          title={expandidas[row.id] ? "Esconde os detalhes desta linha" : "Mostra os detalhes desta linha"}
                          aria-expanded={Boolean(expandidas[row.id])}
                          onClick={() => alternarLinha(row.id)}
                        >
                          <span
                            className={cn(
                              "flex transition-transform motion-reduce:transition-none",
                              expandidas[row.id] && "rotate-90",
                            )}
                          >
                            <Icones.Proxima tamanho={16} />
                          </span>
                        </Button>
                      </TableCell>
                    )}
                    {row.getVisibleCells().map((cell, indice) => (
                      <TableCell
                        key={cell.id}
                        className={cn("break-words whitespace-normal", classeDaColuna(indice, temAcoes), classeDaColunaPelaTela.get(cell.column.id))}
                      >
                        {desenharCelula(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {isMobile && (abertaPelaTela(row.original) ? linhaAberta?.(row.original) === true : expandidas[row.id]) && (
                    <TableRow data-slot="linha-expandida" className="bg-muted hover:bg-muted">
                      <TableCell colSpan={columnDefs.length + (botaoDeExpandir ? 1 : 0)} className="whitespace-normal">
                        <dl className="grid grid-cols-1 gap-3">
                          {colunasDeDados.slice(1).map((coluna) => {
                            const valor = (row.original as Record<string, unknown>)[coluna.name]

                            return (
                              <div key={coluna.name} className="flex flex-col gap-1">
                                <dt className="text-xs font-semibold tracking-wide text-muted-foreground">
                                  {coluna.label ?? coluna.name}
                                </dt>
                                <dd className="text-sm break-words text-foreground">
                                  {coluna.render ? coluna.render(valor, row.original) : String(valor ?? "—")}
                                </dd>
                              </div>
                            )
                          })}
                        </dl>
                      </TableCell>
                    </TableRow>
                  )}
                  {detalheDaLinha !== undefined && linhaAberta?.(row.original) === true && (
                    <TableRow data-slot="detalhe-da-linha" data-listra="nao" className="bg-(--branco) hover:bg-(--branco)">
                      <TableCell colSpan={columnDefs.length + (botaoDeExpandir ? 1 : 0)} className="px-4 py-3 whitespace-normal">
                        {detalheDaLinha(row.original)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columnDefs.length + (botaoDeExpandir ? 1 : 0)}
                  className="h-24 text-center whitespace-normal text-muted-foreground"
                >
                  {mensagemVazia}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {paginacao && (
        <Paginacao
          pagina={currentPage}
          porPagina={pageSize}
          total={totalRegistros}
          aoTrocarPagina={onPageChange}
          aoTrocarPorPagina={onPageSizeChange}
        />
      )}
    </div>
  )
}

export { TablePrincipal2, Column, Acao, TablePrincipalFiltroGlobal, TableFiltrosAvancados }
export type { ColumnProps, AcaoProps, TablePrincipal2Props }
